import { useParams } from "wouter";
import { useState, useEffect } from "react";
import { Project } from "../lib/store";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

export function useUrlProject(authData?: TendererAuth) {
  const params = useParams<{ projectId?: string; companyCode?: string; accessCode?: string }>();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Extract project ID from URL - handle both unified and legacy formats
  // Unified: /dashboard/:projectId/tenderer/:companyCode
  // Legacy: /tenderer/dashboard/:companyCode/:projectId
  const projectId = params?.projectId;
  const companyCode = params?.companyCode || params?.accessCode;
  const accessCode = authData?.accessCode || companyCode;

  useEffect(() => {
    if (!authData) return;

    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/tenderer/accessible-projects?email=${encodeURIComponent(authData.email)}&accessCode=${encodeURIComponent(authData.accessCode)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const projectsData = await response.json();
          setProjects(projectsData);
          
          // Auto-select project if we have a project ID in URL
          if (projectId && projectsData.length > 0) {
            const project = projectsData.find((p: Project) => p.id === projectId);
            if (project) {
              setSelectedProject(project);
            } else {
              // Fallback to first project if URL project not found
              setSelectedProject(projectsData[0]);
            }
          } else if (projectsData.length > 0) {
            // Auto-select first project if no URL project ID
            setSelectedProject(projectsData[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [authData, projectId]);

  return {
    selectedProject,
    setSelectedProject,
    projects,
    loading,
    projectId,
    urlProjectId: projectId, // Add urlProjectId as alias for projectId
    accessCode
  };
}